import React from 'react';
import { Redirect, useParams } from 'react-router-dom';
import PageLayout from 'components/common/PageLayout';
import { LinkWithQuery } from 'components/common/LinkWithQuery';
import {
  AI_ROUTE,
  ANALYTICS_ROUTE,
  BRAND_GOVERNANCE_ROUTE,
  ENTERPRISE_CONTACT_LINK,
  INTEGRATIONS_ROUTE,
  NOT_FOUND_ROUTE,
  STUDIO_ROUTE,
} from 'components/constants';
import { IndustrySolution, industrySolutionBySlug } from './data';
import { LazyVideo } from 'components/common/LazyVideo';
import ProductDemo from './ProductDemo';
import { solutionExamples } from './examples';
import { productWorkflows } from './workflows';
import styles from './styles.module.scss';

type RouteParams = {
  industry: string;
};

type Props = {
  solution?: IndustrySolution;
};

const platformFoundations = [
  {
    number: '01',
    title: 'Professional visual creation',
    text: 'Start with AI or an approved template, then refine every layout, state and breakpoint in the Storycards Studio.',
    route: STUDIO_ROUTE,
    cta: 'Explore the Studio',
  },
  {
    number: '02',
    title: 'Brand and publishing control',
    text: 'Protect components, fields and interactions. Decide who can create, review and publish each experience.',
    route: BRAND_GOVERNANCE_ROUTE,
    cta: 'Explore brand governance',
  },
  {
    number: '03',
    title: 'AI-assisted starting points',
    text: 'Turn an approved brief or source into an editable first version that stays inside your component and permission system.',
    route: AI_ROUTE,
    cta: 'Explore AI creation',
  },
  {
    number: '04',
    title: 'Structured first-party data',
    text: 'Define interaction events and response fields, then connect consented data to the approved destinations in your stack.',
    route: INTEGRATIONS_ROUTE,
    cta: 'Explore integrations',
  },
  {
    number: '05',
    title: 'Measure and improve',
    text: 'See completion, choices and drop-off points so teams can make focused changes to the live journey.',
    route: ANALYTICS_ROUTE,
    cta: 'Explore analytics',
  },
];

const IndustrySolutions: React.FC<Props> = ({ solution }) => {
  const { industry: slug } = useParams<RouteParams>();
  const data = solution || industrySolutionBySlug[slug];

  if (!data) return <Redirect to={NOT_FOUND_ROUTE} />;

  const example = solutionExamples[data.slug];
  const workflows = productWorkflows[data.slug];
  const relatedIndustries = data.related.map((relatedSlug) => industrySolutionBySlug[relatedSlug]);
  const industryStyle = {
    '--industry-accent': data.accent,
    '--industry-soft': data.softAccent,
  } as React.CSSProperties;

  return (
    <PageLayout className={styles.page} isFullWidth isMarketingHeader showFooterCta={false}>
      <main style={industryStyle}>
        <section className={styles.hero}>
          <div className={styles.container}>
            <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
              <LinkWithQuery to="/">Home</LinkWithQuery>
              <span aria-hidden="true">/</span>
              <a href="/#industry-solutions-title">Solutions</a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{data.name}</span>
            </nav>

            <div className={styles.heroGrid}>
              <div className={styles.heroCopy}>
                <span className={styles.eyebrow}>{data.hero.eyebrow}</span>
                <h1>{data.hero.title}</h1>
                <p>{data.hero.text}</p>
                <div className={styles.heroActions}>
                  <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryCta}>
                    Talk to our enterprise team
                  </a>
                  <a href="#products" className={styles.secondaryCta}>
                    See what you can build
                    <span aria-hidden="true">↓</span>
                  </a>
                </div>
              </div>

              {example ? <ProductDemo key={data.slug} example={example} /> : <div className={styles.productPreview} aria-label={`${data.hero.previewLabel} product example`}>
                <div className={styles.previewChrome}>
                  <strong>Storycards</strong>
                  <em>Concept illustration</em>
                </div>
                <div className={styles.previewBody}>
                  <span className={styles.previewLabel}>{data.hero.previewLabel}</span>
                  <div className={styles.previewProgress} aria-hidden="true">
                    <span />
                  </div>
                  <h2>{data.hero.previewQuestion}</h2>
                  <div className={styles.previewOptions}>
                    {data.hero.previewOptions.map((option, index) => (
                      <div className={index === 0 ? styles.selectedOption : undefined} key={option}>
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        <strong>{option}</strong>
                      </div>
                    ))}
                  </div>
                  <div className={styles.previewResult}>
                    <span aria-hidden="true">✓</span>
                    <p>{data.hero.previewResult}</p>
                  </div>
                </div>
              </div>}
            </div>
          </div>
        </section>

        <section className={styles.outcomes} aria-labelledby="outcomes-title">
          <div className={styles.container}>
            <h2 id="outcomes-title">{data.outcomesTitle}</h2>
            <div className={styles.outcomeGrid}>
              {data.outcomes.map((outcome, index) => (
                <article key={outcome.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{outcome.title}</h3>
                  <p>{outcome.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.products} id="products" aria-labelledby="products-title">
          <div className={styles.container}>
            <header className={styles.sectionIntro}>
              <span className={styles.eyebrow}>What you can build</span>
              <h2 id="products-title">{data.productsTitle}</h2>
              <p>{data.productsIntro}</p>
            </header>
            <div className={styles.productGrid}>
              {data.products.map((product, index) => (
                <article key={product.title}>
                  <div className={styles.productNumber}>{String(index + 1).padStart(2, '0')}</div>
                  <div>
                    <h3>{product.title}</h3>
                    <p>{product.text}</p>
                  </div>
                  <span className={styles.productSignal}>{product.signal}</span>
                  {workflows?.[index] && <details className={styles.workflow}>
                    <summary aria-label={`View workflow for ${product.title}`}>View workflow <span aria-hidden="true">+</span></summary>
                    <dl>
                      <dt>Ask for</dt><dd>{workflows[index].input}</dd>
                      <dt>Show and connect</dt><dd>{workflows[index].output}</dd>
                      <dt>Measure</dt><dd>{workflows[index].measure}</dd>
                    </dl>
                  </details>}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.journey} aria-labelledby="journey-title">
          <div className={styles.container}>
            <header className={styles.journeyIntro}>
              <span className={styles.eyebrow}>{data.journey.eyebrow}</span>
              <h2 id="journey-title">{example?.title || data.journey.title}</h2>
              <p>{example?.brief || data.journey.text}</p>
            </header>
            {example ? <div className={styles.launchBrief}>
              <h3>The launch brief</h3>
              <dl>
                <dt>What the visitor shares</dt><dd>{example.fields.join(' · ')}</dd>
                <dt>How the path changes</dt><dd>{example.logic}</dd>
                <dt>Where it goes next</dt><dd>{example.destination}</dd>
                <dt>What to measure</dt><dd>{example.measure}</dd>
              </dl>
              <p className={styles.exampleBoundary}>{example.boundary}</p>
            </div> : <div className={styles.journeySteps}>
              {data.journey.steps.map((step, index) => (
                <article key={step.title}>
                  <div className={styles.stepRail}>
                    <span>{step.label}</span>
                    {index < data.journey.steps.length - 1 && <i aria-hidden="true" />}
                  </div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>}
          </div>
        </section>

        <section className={styles.operating} aria-labelledby="operating-title">
          <div className={styles.container}>
            <header className={styles.sectionIntro}>
              <span className={styles.eyebrow}>How the organization works</span>
              <h2 id="operating-title">{data.operatingTitle}</h2>
              <p>{data.operatingText}</p>
            </header>
            <div className={styles.roleList}>
              {data.roles.map((role, index) => (
                <article key={role.team}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{role.team}</h3>
                  <p>{role.responsibility}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.platform} aria-labelledby="platform-title">
          <div className={styles.container}>
            <header className={styles.platformIntro}>
              <span className={styles.eyebrow}>The Storycards platform</span>
              <h2 id="platform-title">Build the experience. Keep control of the details.</h2>
              <p>
                Design the questions, results and responsive layouts in the Studio. Reuse components across products,
                give teams clear editing permissions and connect the finished journey to your systems.
              </p>
            </header>
            <figure className={styles.studioEvidence}>
              <LazyVideo
                src="https://assts.stories.sc/website/desktop/design.mp4"
                poster="https://assts.stories.sc/website/poster/design.jpg"
                autoplay={false} controls once
                title="Storycards Studio visual editing demonstration"
              />
              <figcaption>The Storycards Studio. A platform demonstration, separate from the illustrative journey above.</figcaption>
            </figure>
            <div className={styles.foundationList}>
              {platformFoundations.map((foundation) => (
                <article key={foundation.title}>
                  <span>{foundation.number}</span>
                  <div>
                    <h3>{foundation.title}</h3>
                    <p>{foundation.text}</p>
                    <LinkWithQuery to={foundation.route}>
                      {foundation.cta}
                      <i aria-hidden="true">→</i>
                    </LinkWithQuery>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.related} aria-labelledby="related-title">
          <div className={styles.container}>
            <div className={styles.relatedHeader}>
              <span className={styles.eyebrow}>Explore more solutions</span>
              <h2 id="related-title">Built for connected customer journeys.</h2>
            </div>
            <div className={styles.relatedGrid}>
              {relatedIndustries.map((relatedIndustry) => (
                <LinkWithQuery to={relatedIndustry.route} key={relatedIndustry.slug}>
                  <span>{relatedIndustry.name}</span>
                  <strong>{relatedIndustry.cardText}</strong>
                  <i aria-hidden="true">→</i>
                </LinkWithQuery>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.finalCta} aria-labelledby="industry-cta-title">
          <div className={styles.container}>
            <div>
              <span className={styles.eyebrow}>Build with Storycards</span>
              <h2 id="industry-cta-title">
                {data.cta?.title || 'Bring us the journey you want to build.'}
              </h2>
            </div>
            <div>
              <p>{
                data.cta?.text ||
                'Bring one customer journey, one product brief or one existing experience. We will help you map what to build and how it fits your operating model.'
              }</p>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryCta}>
                Talk to our enterprise team
              </a>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
};

export default IndustrySolutions;
